import React from 'react';
import {
  Alert, Button, Form, Input, notification,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  put, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import './TokenUserValidationComponent.scss';

const TokenUserValidationComponent = (
  props: {
    targetValidation: {
      federalRegistration: string,
      email: string,
      phone: string,
      responseCodeToken: number,
    },
    modalToken: (status: boolean) => void,
  },
): JSX.Element => {
  const navigate = useNavigate();
  const { targetValidation, modalToken } = props;
  const [formInstance] = Form.useForm();

  /**
   * @name handleNotifys
   * @param {*} response
   * @description mensagens de erros com tratamento decorrente de respostas do back-end
   * @returns retorna mensagens de erro tratadas para informar o usuário
   */
  const handleNotifys = (response: { code: number }): void => {
    switch (response.code) {
      case 1018:
        notification.success({
          message: 'Token validado com sucesso!',
          description: 'Por favor, faça o login novamente.',
        });
        break;

      default:
        break;
    }
  };

  /**
   * @name handleBlackoutEmail
   * @param {*} mail
   * @param {*} positionProviderMail
   * @description esconde o email do usuário em página
   * @returns retorna o email escondido na página para o usuário
   */
  const handleBlackoutEmail = (mail: string, positionProviderMail: number): string => {
    return `${mail.substring(0, 3)}*******${mail.substring(positionProviderMail)}`;
  };

  /**
   * @name handleBlackoutPhone
   * @param {*} phone
   * @param {*} positionProviderPhone
   * @description esconde o telefone do usuário na página
   * @returns retorna o telefone escondido na página para o usuário
   */
  const handleBlackoutPhone = (phone: string, positionProviderPhone: number): string => {
    return `${phone.substring(0, 4)}*******${phone.substring(positionProviderPhone)}`;
  };

  /**
   * @name handleSubmitToken
   * @description manipulação de reposta do backend para o token enviado
   * @returns retorna o envio de token validado ou recusado pelo backend
   */
  const handleSubmitToken = async (values: { token: string }): Promise<void> => {
    const args = {
      federalRegistration: targetValidation.federalRegistration,
      token: values.token,
    };

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = put({
      url: '/auth/validateToken',
      params: args,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1018):
        // "Token validated successfully", 1018
        handleNotifys(response?.body);
        modalToken(false);
        break;

      case (responseError?.body?.code === -1021):
        // "Federal registration not found in sap database", -1021
        notification.error({
          message: 'Erro',
          description: 'CPF/CNPJ não encontrado.',
        });
        break;

      case (responseError?.body?.code === -1023):
        // "Invalid token", -1023
        notification.error({
          message: 'Token inválido!',
          description: 'O token enviado não está correto. Verifique e tente novamente.',
        });
        break;

      case (responseError?.body?.code === -1024):
        // "Expired token", -1024
        notification.error({
          message: 'Erro',
          description: 'Token expirado.',
        });
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: 'Usuário não encontrado.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -1026):
      default:
        notification.error({
          message: 'Erro',
          description: 'Ocorreu um erro.',
        });
        break;
    }
  };

  /**
   * @name renderValidationComponent
   * @description manipulação de conteúdo que será renderizado para o usuário
   * @returns retorna a renderização do conteúdo em página para o usuário
   */
  const renderValidationComponent = (): JSX.Element => {
    let textMessage = '';

    // #region Validação de email
    let blackoutEmail;

    if (
      targetValidation.email != ''
      && targetValidation.email != null
      && targetValidation.email != undefined
    ) {
      blackoutEmail = handleBlackoutEmail(
        targetValidation.email,
        targetValidation.email.indexOf('@'),
      );
      targetValidation.email = blackoutEmail;
    } else {
      blackoutEmail = '***';
    }
    // #endregion

    // #region Validação de Telefone
    let blackoutPhone;
    if (
      targetValidation.phone != ''
      && targetValidation.phone != null
      && targetValidation.phone != undefined
    ) {
      blackoutPhone = handleBlackoutPhone(
        targetValidation.phone,
        targetValidation.phone.length - 2,
      );
      targetValidation.phone = blackoutPhone;
    } else {
      blackoutPhone = '***';
    }
    // #endregion

    if (targetValidation.responseCodeToken === 1020) {
      textMessage = `Enviamos o código de ativação para o telefone ${blackoutPhone}. Favor inserir o código recebido no campo abaixo:`;
    } else if (targetValidation.responseCodeToken === 1021) {
      textMessage = `Enviamos o código de ativação para o e-mail ${blackoutEmail}. Favor inserir o código recebido no campo abaixo:`;
    } else if (targetValidation.responseCodeToken === 1019) {
      textMessage = `Enviamos o código de ativação para o e-mail ${blackoutEmail} e para o telefone ${blackoutPhone}. Favor inserir o código recebido no campo abaixo:`;
    } else {
      textMessage = 'Enviamos o código de ativação. Favor inserir o código recebido no campo abaixo:';
    }

    return (
      <div>
        <div>
          <Alert
            message={textMessage}
            type="info"
            showIcon
          />
        </div>

        <div className="ValidationComponent__Form">
          <Form
            name="formTokenValidation"
            form={formInstance}
            onFinish={handleSubmitToken}
            onFinishFailed={
              (errorInfo): void => {
                console.log('onFinishFailed', errorInfo);
              }
            }
            autoComplete="off"
          >
            <Form.Item
              label="Código de ativação"
              name="token"
              rules={[
                {
                  required: true,
                  message: 'Por favor, insira o token de validação.',
                },
              ]}
            >
              <Input />
            </Form.Item>

            <div className="ValidationComponent__ButtonSubmitSection">
              <Form.Item className="ValidationComponent__ButtonSubmit">
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={
                    (): void => {
                      formInstance.submit();
                    }
                  }
                >
                  Verificar token
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>
      </div>
    );
  };

  return (
    <div className="ValidationComponent">
      {renderValidationComponent()}
    </div>
  );
};

TokenUserValidationComponent.propTypes = {
  targetValidation: PropTypes.shape({
    federalRegistration: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    responseCodeToken: PropTypes.number,
  }).isRequired,
  modalToken: PropTypes.func.isRequired,
};

TokenUserValidationComponent.defaultProps = {
};

export default TokenUserValidationComponent;
